// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ChevronsUpDown from "@insite/mobius/Icons/ChevronsUpDown";
import Search from "@insite/mobius/Icons/Search";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import X from "@insite/mobius/Icons/X";
import { myListsDetailsOptionsStyles } from "@sc/Overrides/Widgets/MyListsDetails/MyListsDetailsOptions";
import Close from "@sc/XDL/Icons/Close";
import Plus from "@sc/XDL/Icons/Plus";
import { createLinkIconStyles, generateButtonLinkStyle, mergeWithCss, xScreen } from "@sc/XDL/Utilities";
import { css } from "styled-components";

myListsDetailsOptionsStyles.wrapper = {
    css: css`
        @media print {
            display: none;
        }
        display: flex;
        margin-bottom: 16px;
    `,
};
myListsDetailsOptionsStyles.leftColumnWrapper = {
    css: css`
        display: flex;
        width: 100%;
        gap: 10px;
        ${xScreen("gt", "md")} {
            align-items: end;
        }
        ${xScreen("lte", "sm")} {
            flex-direction: column;
        }
    `,
};
myListsDetailsOptionsStyles.container = {
    gap: 20,
    offsetCss: css`
        justify-content: space-between;
    `,
};
myListsDetailsOptionsStyles.sortOrderGridItem = { width: 12 };
myListsDetailsOptionsStyles.searchGridItem = { width: [12, 12, 6, 3, 3] };
myListsDetailsOptionsStyles.search = {
    iconProps: {
        src: Search,
        color: "common.ui7",
    },
};
myListsDetailsOptionsStyles.sortByGridItem = {
    width: [12, 12, 6, 3, 3],
};
myListsDetailsOptionsStyles.sortBySelect = {
    cssOverrides: {
        formField: css`
            label {
                width: auto;
            }
        `,
        formInputWrapper: css`
            flex: 1;
        `,
    },
};
myListsDetailsOptionsStyles.selectAllGridItem = {
    width: [12, 12, 12, 6, 6],
    css: css`
        align-items: end;
        ${xScreen("gt", "sm")} {
            padding-bottom: 22px;
        }
    `,
};
myListsDetailsOptionsStyles.selectAllWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 32px;
    `,
};
myListsDetailsOptionsStyles.addItemsGridItem = {
    width: [12, 4, 6, 3, 3],
    css: css`
        align-items: flex-end;
        justify-content: flex-end;
        ${xScreen("lte", "xs")} {
            justify-content: flex-start;
        }
    `,
};
myListsDetailsOptionsStyles.addItemsButton = {
    variant: "primary",
    icon: {
        src: Plus,
        position: "right",
    },
    css: css`
        align-items: baseline;
        white-space: nowrap;
        padding: 10px 20px;
        ${xScreen("lte", "sm")} {
            width: 100%;
        }
    `,
};
myListsDetailsOptionsStyles.uploadItemsModal = {
    sizeVariant: "large",
};
myListsDetailsOptionsStyles.actionsWrapper = {
    css: css`
        display: inline-flex;
        gap: 10px;
        ${xScreen("lte", "sm")} {
            align-items: flex-end;
            justify-content: space-between;
            margin-top: 10px;
        }
    `,
};
myListsDetailsOptionsStyles.uploadItemsButton = {
    variant: "secondary",
    css: css`
        white-space: nowrap;
        padding-block: 11px;
    `,
};
myListsDetailsOptionsStyles.closeButton = {
    ...mergeWithCss(generateButtonLinkStyle(Close), {
        css: css`
            ${xScreen("lte", "sm")} {
                margin-bottom: 13px;
            }
        `,
    }),
};
myListsDetailsOptionsStyles.actionButtonsHidden = {
    css: css`
        ${xScreen("lte", "sm")} {
            margin-top: 20px;
        }
    `,
};
myListsDetailsOptionsStyles.productSelectorStyles = {
    container: {
        gap: 10,
        offsetCss: css`
            row-gap: 5px;
        `,
    },
    buttonGridItem: {
        width: [12, 5, 3, 3, 3],
        css: css`
            justify-content: start;
            align-items: end;
            &&&& {
                ${xScreen("lte", "sm")} {
                    max-width: none;
                    flex: 1;
                }
            }
        `,
    },
    selectButton: {
        css: css`
            padding-block: 11px;
            margin-bottom: 0;
            &&&& {
                ${xScreen("lte", "sm")} {
                    margin-top: 0;
                    min-width: 150px;
                }
            }
        `,
    },
    unitOfMeasureGridItem: {
        width: [5, 4, 3, 3, 3],
        css: css`
            &:empty {
                display: none;
            }
            &&& {
                ${xScreen("lte", "sm")} {
                    max-width: none;
                    min-width: 100px;
                    flex: 1;
                }
            }
        `,
    },
};

myListsDetailsOptionsStyles.editSortOrder = {
    css: css`
        cursor: pointer;
    `,
};
myListsDetailsOptionsStyles.addSelectedToCartLink = {
    ...createLinkIconStyles(ShoppingCart, {
        icon: {
            iconProps: {
                size: 12,
                css: css`
                    margin-right: 6px;
                `,
            },
        },
        css: css`
            display: flex;
            justify-content: center;
            &:disabled {
                opacity: 0.33;
            }
        `,
    }),
};
myListsDetailsOptionsStyles.deleteSelectedLink = {
    ...createLinkIconStyles(X, {
        icon: {
            iconProps: {
                size: 12,
                css: css`
                    margin-right: 6px;
                `,
            },
        },
        css: css`
            display: flex;
            justify-content: center;
            &:disabled {
                opacity: 0.33;
            }
        `,
    }),
};
myListsDetailsOptionsStyles.editSortOrderLink = {
    ...createLinkIconStyles(ChevronsUpDown, {
        icon: {
            iconProps: {
                size: 12,
                css: css`
                    margin-right: 6px;
                `,
            },
        },
        css: css`
            display: flex;
            justify-content: center;
        `,
    }),
};
